import { format, parse } from 'date-fns';
import type {
  AvailableAfe,
  PdpDeclineForecast,
  TypeCurveForecast,
} from 'types/api-responses';
import type { TractNRI } from '../types';
import type { BulkEditFormState } from './types';

/**
 * DOM id of the AFE picker's combobox input. Shared so the id lives in ONE place: the
 * `EditFieldsView` combobox that renders it, and the panel's `selectorPrimaryFocus` (which
 * must target that input to keep the menu open during a select-all catalog fetch). A string
 * literal duplicated across the two files would silently break the focus fix if renamed.
 */
export const AFE_PICKER_INPUT_ID = 'bulk-edit-afe';

/** Human-readable label for each field key. Used in the review header row. */
export const FIELD_LABELS: Record<keyof BulkEditFormState, string> = {
  afeSourceId: 'AFE',
  permitDate: 'Permit Date',
  spudDate: 'Spud Date',
  firstProductionDate: 'First Production Date',
  contingenciesCostsDate: 'Contingency Cost Date',
  facilitiesCostsDate: 'Facility Cost Date',
  drillingCostsDate: 'Drilling Cost Date',
  completionCostsDate: 'Completion Cost Date',
  otherCostsDate: 'Other Cost Date',
  // Only volumeSourceId is reviewed (see REVIEW_FIELD_ORDER). The other four travel with
  // it as mechanism rather than as separate choices — the type routes the calculator, the
  // href and scenario are display — so listing them would read as five edits, not one.
  volumeSourceId: 'Volume Source',
  volumeSourceType: 'Volume Source Type',
  volumeSourceHref: 'Volume Source Link',
  forecastSource: 'Forecast Source',
  dcaScenario: 'DCA Scenario',
};

/** Stable column order in the review table — matches the edit form order. */
export const REVIEW_FIELD_ORDER: Array<keyof BulkEditFormState> = [
  'afeSourceId',
  'contingenciesCostsDate',
  'facilitiesCostsDate',
  'drillingCostsDate',
  'completionCostsDate',
  'spudDate',
  'permitDate',
  'firstProductionDate',
  'otherCostsDate',
  'volumeSourceId',
];

const DATE_KEYS = new Set<keyof BulkEditFormState>([
  'permitDate',
  'spudDate',
  'firstProductionDate',
  'contingenciesCostsDate',
  'facilitiesCostsDate',
  'drillingCostsDate',
  'completionCostsDate',
  'otherCostsDate',
]);

/**
 * Display a yyyy-MM-dd date string as MM/DD/YYYY; pass through anything else.
 *
 * @param {string} value - The date string to format.
 * @return {string} The formatted date string.
 */
const formatDateValue = (value: string): string => {
  try {
    return format(parse(value, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy');
  } catch {
    return value;
  }
};

/**
 * Returns a callable that looks up an AFE id and returns its display label
 * (well name + AFE id where available), falling back to the raw id.
 *
 * @param {AvailableAfe[]} items - The list of available AFEs.
 * @return {Function} A lookup function `(id?: string) => string`.
 */
export const makeAfeLookup =
  (items: AvailableAfe[]) =>
  (id?: string): string => {
    if (!id) return '—';
    const found = items.find((a) => a.id === id);
    if (!found) return id;
    const wellName = found.well?.name;
    return wellName ? `${wellName} · ${found.id}` : found.id;
  };

/**
 * Returns a callable that looks up a volume-source id — a type curve or a PDP
 * decline — and returns the display name the user picked it by, falling back to
 * the raw id when the forecasts aren't loaded or the id is no longer among them.
 *
 * @param {Object|null} forecasts - The picker's loaded forecast lists, or null.
 * @return {Function} A lookup function `(id?: string) => string`.
 */
export const makeVolumeSourceLookup =
  (
    forecasts: {
      typeCurves: TypeCurveForecast[];
      pdpDeclines: PdpDeclineForecast[];
    } | null
  ) =>
  (id?: string): string => {
    if (!id) return '—';
    const found =
      forecasts?.typeCurves.find((f) => f.id === id) ??
      forecasts?.pdpDeclines.find((f) => f.id === id);
    return found?.name ?? id;
  };

/**
 * Formats a single field value for display in the review grid. Empty values
 * render as an em-dash; dates are humanized; AFE ids run through the AFE lookup;
 * volume-source ids run through the forecast lookup (so a PDP decline reviews as
 * the name the user picked, not its numeric id).
 *
 * @param {string} key - The field key being formatted.
 * @param {string} value - The raw value (or undefined/empty).
 * @param {Function} afeLookup - AFE id → label lookup.
 * @param {Function} volumeSourceLookup - Volume-source id → name lookup; defaults
 *   to echoing the id, so callers without loaded forecasts keep today's output.
 * @return {string} The display string for the cell.
 */
export const formatFieldValue = (
  key: keyof BulkEditFormState,
  value: string | undefined,
  afeLookup: (id?: string) => string,
  volumeSourceLookup: (id?: string) => string = (id) => id ?? '—'
): string => {
  if (value === undefined || value === '' || value === null) return '—';
  if (key === 'afeSourceId') return afeLookup(value);
  if (key === 'volumeSourceId') return volumeSourceLookup(value);
  if (DATE_KEYS.has(key)) return formatDateValue(value);
  return value;
};

export interface ReviewRow {
  [key: string]: unknown;
  slotId: string;
  fields: Partial<
    Record<
      keyof BulkEditFormState,
      { originalDisplay: string; newDisplay: string; changed: boolean }
    >
  >;
}

/**
 * Builds rows for the review table: one row per selected slot, with a cell
 * for every field in the patch. The "original" value is read directly from
 * the TractNRI row — for keys that map 1:1 onto TractNRI field names
 * (which all 9 do, by design — see types.ts). Callers that maintain
 * additional override state should compose their resolver upstream of the
 * `originalResolver` argument.
 *
 * @param {TractNRI[]} selectedRows - The rows currently selected.
 * @param {Object} patch - The fields the user populated.
 * @param {Function} afeLookup - AFE id → label lookup.
 * @param {Function} originalResolver - Resolves the "current" value for a field on a row.
 * @param {Function} volumeSourceLookup - Volume-source id → name lookup (see formatFieldValue).
 * @return {ReviewRow[]} One review row per slot with truthy slotId.
 */
export const buildReviewRows = (
  selectedRows: TractNRI[],
  patch: Partial<BulkEditFormState>,
  afeLookup: (id?: string) => string,
  originalResolver: (
    row: TractNRI,
    key: keyof BulkEditFormState
  ) => string | undefined = (row, key) => row[key],
  volumeSourceLookup: (id?: string) => string = (id) => id ?? '—'
): ReviewRow[] => {
  const fieldKeys = (
    Object.keys(patch) as Array<keyof BulkEditFormState>
  ).filter((k) => patch[k] !== undefined && patch[k] !== '');

  return selectedRows
    .filter((row) => Boolean(row.slotId))
    .map((row) => {
      const fields: ReviewRow['fields'] = {};
      fieldKeys.forEach((key) => {
        const original = originalResolver(row, key);
        const next = patch[key];
        const originalDisplay = formatFieldValue(
          key,
          original,
          afeLookup,
          volumeSourceLookup
        );
        const newDisplay = formatFieldValue(
          key,
          next,
          afeLookup,
          volumeSourceLookup
        );
        fields[key] = {
          originalDisplay,
          newDisplay,
          changed: original !== next,
        };
      });
      return { slotId: row.slotId as string, fields };
    });
};
